'use client';

import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ValidationError, ValidationErrorComponent } from '@/components/validation-error/validation-error';
import { regexes } from '@/constants/regexes';
import { theaterSearch } from '@/constants/theater-search';
import { theaterApiService } from '@/services/(frontend)/theaters/theater-api.service';
import { createFormErrorHandler } from '@/utils/form-error-handler';
import { PagedListOfTheater } from '@cinemark/content-api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

type MoviesZipCodeSearchProps = {
  onCancel: () => void;
  onResults: (results: PagedListOfTheater, zipCode: string) => void;
};

const formSchema = z.object({
  zipCode: z.string({
    message: 'ZIP code cannot be empty.',
  }).regex(regexes.zipCode, {
    message: 'The value for the ZIP Code field is invalid.',
  }),
});

export const MoviesZipCodeSearch = ({ onCancel, onResults }: Readonly<MoviesZipCodeSearchProps>): React.JSX.Element => {
  const searchId = useRef(0);
  const [searching, setSearching] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      zipCode: '',
    },
    resolver: zodResolver(formSchema),
  });
  const handleFormErrors = createFormErrorHandler<z.infer<typeof formSchema>>(setValidationErrors);

  const onSubmit = async (values: z.infer<typeof formSchema>): Promise<void> => {
    const activeSearchId = searchId.current + 1;
    searchId.current = activeSearchId;
    setSearching(true);
    setValidationErrors([]);

    try {
      const response = await theaterApiService.getTheatersByZip({
        pageSize: theaterSearch.limit,
        radius: theaterSearch.radius,
        zipcode: values.zipCode,
      });
      if (activeSearchId !== searchId.current) return;

      onResults(response.data, values.zipCode);
    } catch {
      if (activeSearchId !== searchId.current) return;

      setValidationErrors([{ messages: ['We were unable to search and find the theatre closest to you by your zip code.'] }]);
    }

    setSearching(false);
  };

  const handleCancel = (): void => {
    searchId.current += 1;
    setSearching(false);
    onCancel();
  };

  return (
    <div className="flex flex-col gap-sm w-full">
      <ValidationErrorComponent errors={validationErrors} />

      <form onSubmit={form.handleSubmit(onSubmit, handleFormErrors)}>
        <Controller control={form.control}
          name="zipCode"
          render={({ field, fieldState }) =>
            <Field className="w-[75%] self-center md:w-full">
              <FieldLabel required
                error={!!fieldState.error}
                htmlFor="movies-input-zip-code"
              >
                Search by ZIP Code
              </FieldLabel>
              <Input {...field}
                aria-invalid={fieldState.invalid}
                data-test="movies-input-zip-code"
                id="movies-input-zip-code"
                maxLength={5}
                type="number"
              />
            </Field>
          }
        />

        <div className="flex gap-sm mt-sm">
          <Button className="flex-1"
            data-test="movies-button-zip-code-cancel"
            type="button"
            variant="outline"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button className="flex-1"
            data-test="movies-button-zip-code-search"
            disabled={searching}
            loadingCenter="within"
            showLoading={searching}
            type="submit"
          >
            {searching ? 'Searching' : 'Search'}
          </Button>
        </div>
      </form>
    </div>
  );
};
